import { Injectable, inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { ToasterService } from '@abp/ng.theme.shared';

/**
 * Custom HTTP error handler that shows server errors as toast notifications
 * instead of ABP's default modal dialog.
 *
 * Implements ABP's CustomHttpErrorHandlerService interface.
 * Priority > ABP's AbpFormatErrorHandlerService (30) so this runs first.
 */
@Injectable({ providedIn: 'root' })
export class ToastErrorHandlerService {
  readonly priority = 100;

  private toasterService = inject(ToasterService);
  private error!: HttpErrorResponse;

  canHandle(error: HttpErrorResponse): boolean {
    this.error = error;

    // Don't handle 401 (ABP redirects to login)
    if (error.status === 401) {
      return false;
    }

    // Handle ABP-formatted errors (UserFriendlyException, validation errors, etc.)
    const abpError = error?.error?.error;
    if (abpError?.message || abpError?.validationErrors?.length) {
      return true;
    }

    // Handle other common HTTP errors
    if (error.status === 403 || error.status === 404 || error.status === 429 || error.status >= 500) {
      return true;
    }

    return false;
  }

  execute(): void {
    const abpError = this.error?.error?.error;

    // Validation errors (multiple field errors)
    if (abpError?.validationErrors?.length) {
      const messages = abpError.validationErrors
        .map((e: any) => e.message)
        .filter(Boolean)
        .join('\n');
      this.toasterService.error(messages, 'Doğrulama Hatası', { life: 7000 });
      return;
    }

    // ABP formatted error with message
    if (abpError?.message) {
      this.toasterService.error(abpError.message, 'Hata', { life: 6000 });
      return;
    }

    // HTTP status code based messages
    switch (this.error.status) {
      case 403:
        this.toasterService.error('Bu işlem için yetkiniz bulunmamaktadır.', 'Yetkisiz İşlem', { life: 5000 });
        break;
      case 404:
        this.toasterService.error('İstenen kaynak bulunamadı.', 'Bulunamadı', { life: 5000 });
        break;
      case 429:
        this.toasterService.error('Çok fazla istek gönderildi. Lütfen biraz bekleyin.', 'İstek Limiti', { life: 7000 });
        break;
      default:
        this.toasterService.error('Beklenmeyen bir sunucu hatası oluştu.', 'Sunucu Hatası', { life: 6000 });
        break;
    }
  }
}
